// services/api.js
import axios from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import { v4 as uuidv4 } from 'uuid';
import axiosInstance from './axiosInstance';

// URLs de production - JAMAIS utiliser back-pharos.mbscom.net
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://varras.mbscom.net/api';
let FRONTEND_URL = process.env.REACT_APP_API_URL_FRONT || 'https://varras.mbscom.net/api/frontoffice';

// Validation et correction automatique pour éviter les mauvaises URLs
if (FRONTEND_URL.includes('back-pharos.mbscom.net')) {
  console.error('⚠️ ATTENTION: URL incorrecte détectée dans api.js! Correction automatique appliquée.');
  FRONTEND_URL = FRONTEND_URL.replace('back-pharos.mbscom.net', 'varras.mbscom.net');
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Variable pour éviter les refreshs multiples simultanés
let refreshPromise = null;

// Intercepter les requêtes pour ajouter le token JWT si disponible
api.interceptors.request.use(
  (config) => {
    // Ignorer l'ajout du token pour les requêtes de refresh
    if (config.skipAuthRefresh || config.url.includes('/token/refresh')) {
      config.headers['X-Correlation-ID'] = uuidv4();
      return config;
    }
    
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['X-Correlation-ID'] = uuidv4();
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Fonction de refresh pour l'instance api
const refreshAuthLogic = failedRequest => {
  // Ignorer les requêtes OPTIONS (CORS preflight)
  if (failedRequest?.response?.config?.method?.toUpperCase() === 'OPTIONS') {
    return Promise.reject(failedRequest);
  }

  // Si un refresh est déjà en cours, attendre qu'il se termine
  if (refreshPromise) {
    return refreshPromise.then(() => {
      const token = localStorage.getItem('token');
      if (token) {
        failedRequest.response.config.headers['Authorization'] = 'Bearer ' + token;
        return Promise.resolve();
      }
      return Promise.reject(new Error("Token non disponible après refresh"));
    });
  }

  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    // Ne rediriger que si on n'est pas déjà sur la page de login et si on a pas été lancé par skipAuthRefresh
    if (window.location.pathname !== "/login" && !failedRequest.response.config.skipAuthRefresh) {
      localStorage.removeItem('token');
      window.location.href = '/login?error=session_expired';
    }
    return Promise.reject(new Error("Aucun refresh token"));
  }

  // Créer une nouvelle instance axios sans interceptors pour éviter les boucles
  const refreshAxios = axios.create({
    baseURL: FRONTEND_URL,
    headers: { 'Content-Type': 'application/json' }
  });

  refreshPromise = refreshAxios.post('/token/refresh', {
    refresh_token: refreshToken
  }).then(response => {
    const newToken = response.data.token;
    localStorage.setItem('token', newToken);
    
    // Mettre à jour le header de la requête qui a échoué
    failedRequest.response.config.headers['Authorization'] = 'Bearer ' + newToken;
    refreshPromise = null;
    return Promise.resolve();
  }).catch(err => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    refreshPromise = null;
    
    if (window.location.pathname !== "/login") {
      window.location.href = '/login?error=session_expired';
    }
    return Promise.reject(err);
  });

  return refreshPromise;
};

// Configurer l'interceptor avec des options spécifiques
createAuthRefreshInterceptor(api, refreshAuthLogic, {
  statusCodes: [401],
  retryInstance: api,
  pauseInstanceWhileRefreshing: true
});

export const getAllSpectaleSpectacle = async (typeShow) => {
  try {
    const response = await api.get(`/spectacles`);
    return response.data;
  } catch (error) {
    console.error('Erreur getUpcomingSpectacles :', error);
    throw error;
  }
};

export const getFeaturedSpectacle = async (typeShow) => {
  try {
    const response = await api.get(`/spectacles/read/oneFeatured/${typeShow}`);
    return response.data;
  } catch (error) {
    console.error('Erreur getFeaturedSpectacle :', error);
    throw error;
  }
};

export const getUpcomingSpectacles = async (typeShow) => {
  try {
    const response = await api.get(`/spectacles/read/${typeShow}`);
    return response.data;
  } catch (error) {
    console.error('Erreur getUpcomingSpectacles :', error);
    throw error;
  }
};



// Méthode pour obtenir les infos utilisateur
export const getUserProfiles = async () => {
  try {
    const response = await axiosInstance.get('/customer/get');
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des profils utilisateur:', error);
    throw error;
  }
};

// Méthode pour obtenir les infos utilisateur
export const getFile = async (url) => {
  try {
    const response = await axiosInstance.get(url, { responseType: 'blob' });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des profils utilisateur:', error);
    throw error;
  }
};

// Méthode pour modifier les infos utilisateur
export const UpdateUserProfiles = async (data) => {
  try {
    const response = await axiosInstance.put('/customer/update', {
      ...data,
    });
    return { success: false, data: response.data };
  } catch (error) {

    return { success: false, errors: error.response.data };
  }
};

// Fonctions pour le streaming
export const accessToStream = async ({ accessCode }) => {
  try {
    const response = await axiosInstance.post("/stream/access", {
      accessCode
    });
    console.log('response.data', response.data)
    return { success: true, data: response.data };
  } catch (error) {
    console.log('error.response', error.response.data)
    if (error.response && error.response.data) {
      // Return error details from backend (e.g., field errors)
      return { success: false, errors: error.response.data };
    }
    // Return generic error if no response data
    return { success: false, errors: { message: 'Une erreur est survenue.' } };
  }
};

export const pingStream = async ({ sessionId }) => {
  try {
    const response = await axiosInstance.post("/stream/ping", {
      sessionId
    });
    return { success: true, data: response.data };
  } catch (error) {
    if (error.response && error.response.data) {
      return { success: false, errors: error.response.data };
    }
    return { success: false, errors: { message: 'Une erreur est survenue.' } };
  }
};

export const getUserStream = async () => {
  try {
    const response = await axiosInstance.get("/stream/get-all-codes");
    return { success: true, data: response.data };
  } catch (error) {
    if (error.response && error.response.data) {
      // Return error details from backend (e.g., field errors)
      return { success: false, errors: error.response.data };
    }
    // Return generic error if no response data
    return { success: false, errors: { message: 'Une erreur est survenue.' } };
  }
};
// Méthode pour obtenir les adresses utilisateur
export const getUserAddresses = async () => {
  try {
    const response = await axiosInstance.get('/address/browse');
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des profils utilisateur:', error);
    throw error;
  }
};


export const crearteAddress = async ({ city, zipCode, address, firstname, lastname, name, phone, email, country, raisonSociale, siret, numTva }) => {
  try {
    const response = await axiosInstance.post('/address/create', {
      city,
      zipCode,
      address,
      name,
      firstname,
      lastname,
      phone,
      email,
      country,
      raisonSociale,
      siret,
      numTva
    });
    return { success: true, data: response.data };
  } catch (error) {
    if (error.response && error.response.data) {
      // Return error details from backend (e.g., field errors)
      return { success: false, errors: error.response.data };
    }
    // Return generic error if no response data
    return { success: false, errors: { message: 'Une erreur est survenue.' } };
  }
};
export const updateAddress = async ({ id, city, zipCode, address, firstname, lastname, name, phone, email, country, raisonSociale, siret, numTva }) => {
  try {
    const response = await axiosInstance.put(`/address/update/${id}`, {
      city,
      zipCode,
      address,
      name,
      firstname,
      lastname,
      phone,
      email,
      country,
      raisonSociale,
      siret,
      numTva
    });
    return { success: true, data: response.data };
  } catch (error) {
    if (error.response && error.response.data) {
      // Return error details from backend (e.g., field errors)
      return { success: false, errors: error.response.data };
    }
    // Return generic error if no response data
    return { success: false, errors: { message: 'Une erreur est survenue.' } };
  }
};
export const setAddressToCart = async ({ addressId }) => {
  console.log("🚀 DEBUG API: Début setAddressToCart", { addressId });
  
  try {
    console.log("📡 DEBUG API: Envoi requête POST /cart/set-address");
    const response = await axiosInstance.post("/cart/set-address", {
      addressId
    });
    
    console.log("✅ DEBUG API: Réponse setAddressToCart reçue", {
      status: response.status,
      data: response.data
    });
    
    return { success: true, data: response.data };
  } catch (error) {
    console.error("❌ DEBUG API: Erreur setAddressToCart", {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
      addressId
    });
    
    if (error.response && error.response.data) {
      // Return error details from backend (e.g., field errors)
      return { success: false, errors: error.response.data };
    }
    // Return generic error if no response data
    return { success: false, errors: { message: 'Une erreur est survenue.' } };
  }
};
export const getCartAddress = async () => {
  try {
    const response = await axiosInstance.get("/cart/get-address");
    return { success: true, data: response.data };
  } catch (error) {
    if (error.response && error.response.data) {
      // Return error details from backend (e.g., field errors)
      return { success: false, errors: error.response.data };
    }
    // Return generic error if no response data
    return { success: false, errors: { message: 'Une erreur est survenue.' } };
  }
};


export const getCountries = async () => {
  try {
    const response = await axiosInstance.get("/country/list");
    return { success: true, data: response.data };
  } catch (error) {
    if (error.response && error.response.data) {
      // Return error details from backend (e.g., field errors)
      return { success: false, errors: error.response.data };
    }
    // Return generic error if no response data
    return { success: false, errors: { message: 'Une erreur est survenue.' } };
  }
};



export const startPayment = async () => {
  console.log("🚀 DEBUG API: Début startPayment");
  
  try {
    console.log("📡 DEBUG API: Envoi requête POST /payment/payfip/start");
    const response = await axiosInstance.post("/payment/payfip/start");
    
    console.log("✅ DEBUG API: Réponse startPayment reçue", {
      status: response.status,
      data: response.data
    });
    
    return { success: true, data: response.data };
  } catch (error) {
    console.error("❌ DEBUG API: Erreur startPayment", {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
      config: error.config
    });
    
    if (error.response && error.response.data) {
      // Return error details from backend (e.g., field errors)
      return { success: false, errors: error.response.data };
    }
    // Return generic error if no response data
    return { success: false, errors: { message: 'Une erreur est survenue.' } };
  }
};

export const getUserCommands = async () => {
  try {
    const response = await axiosInstance.get("/order");
    return { success: true, data: response.data };
  } catch (error) {
    if (error.response && error.response.data) {
      // Return error details from backend (e.g., field errors)
      return { success: false, errors: error.response.data };
    }
    // Return generic error if no response data
    return { success: false, errors: { message: 'Une erreur est survenue.' } };
  }
};
export const getUserTickets = async () => {
  try {
    const response = await axiosInstance.get("/ticket/list");
    return { success: true, data: response.data };
  } catch (error) {
    if (error.response && error.response.data) {
      // Return error details from backend (e.g., field errors)
      return { success: false, errors: error.response.data };
    }
    // Return generic error if no response data
    return { success: false, errors: { message: 'Une erreur est survenue.' } };
  }
};



// Méthode pour vérifier l'événement spécial
export const checkSpecialEvent = async () => {
  try {
    const response = await api.get('/special_event');
    return response.data; // Supposons que la réponse est { special_event: true/false, widget: { ... } }
  } catch (error) {
    console.error('Erreur lors de la vérification de l\'événement spécial:', error);
    throw error;
  }
};

// Nouvelle méthode pour obtenir les DesignSettings
export const getDesignSettings = async () => {
  try {
    const response = await api.get('/settings/design');
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des paramètres de design:', error);
    throw error;
  }
};

/**
 * Récupère les infos du footer (slug=main par défaut, ou subsite=xxx).
 */
export const getFooter = async (subsiteSlug = '') => {
  const url = subsiteSlug ? `/footer/${subsiteSlug}` : '/footer';
  const response = await api.get(url);
  return response.data;
};
// Vous pouvez ajouter d'autres méthodes API ici


/**
 * Méthode pour checker la disponibilité d'un spectacle
 */
export const checkAvailability = async ({ articleId, quantity }) => {
  return (true);
};

export const getCart = async () => {
  const CART = [
    {
      id: 1,
      title: "Titre de représentation",
      date: "Vendredi 14 Mars 2025 à 20h00",
      description: "Le Pharos, Arras",
      price: 6.80,
      image: `${process.env.REACT_APP_MEDIA_BASE_URL_UPLOAD}/uploads/images/spectacle-2-6818edb9b4bbb509523780.webp`,
      quantity: 1
    },
    {
      id: 2,
      title: "Titre de représentation",
      date: "Vendredi 14 Mars 2025 à 20h00",
      description: "Le Pharos, Arras",
      price: 7.40,
      image: `${process.env.REACT_APP_MEDIA_BASE_URL_UPLOAD}/uploads/images/spectacle-2-6818edb9b4bbb509523780.webp`,
      quantity: 1
    },
    {
      id: 3,
      title: "Titre de représentation",
      date: "Vendredi 14 Mars 2025 à 20h00",
      description: "Le Pharos, Arras",
      price: 5.00,
      image: `${process.env.REACT_APP_MEDIA_BASE_URL_UPLOAD}/uploads/images/spectacle-2-6818edb9b4bbb509523780.webp`,
      quantity: 1
    },
  ]
  return CART;
};

export default api;
